const toNumberOrFallback = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const hasOwn = (source, field) => (
  source != null && Object.prototype.hasOwnProperty.call(source, field)
);

const hasExplicitCanonicalField = (data, field, marker) => {
  if (hasOwn(data, marker)) {
    return data[marker] === true;
  }

  return hasOwn(data, field) || hasOwn(data?.onboardingImportStatus, field);
};

export const INITIAL_CANONICAL_BANK_STATUS = {
  connected: false,
  lastSyncAt: null,
  linkedAccountsCount: 0,
  lastSyncNewCount: null,
  lastSyncUncategorizedCount: null,
  lastSyncTransactionIds: null,
  lastSyncStatus: null,
  lastSyncSource: null,
  needsReconnect: false,
  reconnectItemId: null,
  hasSyncError: false,
  syncErrorItemId: null,
  needsAccountReview: false,
  reviewItemId: null,
  onboardingImportState: 'not_started',
  onboardingImportStartedAt: null,
  currentImportTransactionCount: null,
  recentImportReady: false,
  connectionConfirmed: false,
  connectedAccountsReady: false,
  importQueued: false,
  importRunning: false,
  recentTransactionsImported: false,
  recentHistoryReady: false,
  datasetState: 'waiting_for_connection',
  datasetSettled: false,
  datasetHasPersistedData: false,
  reportingDataReady: false,
  currentMonthKey: null,
  currentMonthReady: false,
  currentMonthTransactionCount: 0,
  importedMonthKeys: [],
  olderMonthsReady: false,
  dashboardDataReady: false,
  dashboardReady: false,
  reviewQueueReady: false,
  canContinueOnboardingFromImportStep: false,
  historicalImportStatus: 'not_started',
  historicalImportInProgress: false,
  importTickerEvents: [],
  historicalImportComplete: false,
  budgetAutoBuildReady: false,
  transactionImportLocked: false,
  aiRefinementLocked: false,
  dataPreparationLocked: false,
  reportsReady: false,
  hasExplicitTransactionImportLocked: false,
  hasExplicitAiRefinementLocked: false,
  hasExplicitDataPreparationLocked: false,
  hasExplicitReportsReady: false,
  hasExplicitBankImportMutationLocked: false,
  bankImportMutationLocked: false,
  aiCategorizationPromptReady: false,
  onboardingImportStatus: {
    phase: 'not_started',
    experienceContext: 'none',
    phaseLabel: 'Not started',
    canEnterDashboard: false,
    recentImportReady: false,
    connectionConfirmed: false,
    connectedAccountsCount: 0,
    connectedAccountsReady: false,
    importQueued: false,
    importRunning: false,
    recentTransactionsImported: false,
    recentHistoryReady: false,
    datasetState: 'waiting_for_connection',
    datasetSettled: false,
    datasetHasPersistedData: false,
    reportingDataReady: false,
    currentMonthKey: null,
    currentMonthReady: false,
    currentMonthTransactionCount: 0,
    importedMonthKeys: [],
    olderMonthsReady: false,
    dashboardDataReady: false,
    dashboardReady: false,
    reviewQueueReady: false,
    reviewQueueCandidateCount: 0,
    currentImportTransactionCount: 0,
    canContinueOnboardingFromImportStep: false,
    historicalImportStatus: 'not_started',
    historicalImportInProgress: false,
    importTickerEvents: [],
    reviewReady: false,
    canUseBudgetAutobuild: false,
    transactionImportLocked: false,
    aiRefinementLocked: false,
    dataPreparationLocked: false,
    reportsReady: false,
    bankImportMutationLocked: false,
    dashboardPresentationState: 'idle',
    aiPhase: 'idle',
    aiRemainingCount: 0,
    retrying: false,
    failed: false,
    failureReason: null,
    activeInstitutionName: null,
    queuedImportCount: 0,
    lastUpdatedAt: null,
  },
  initialImportBackgroundCategorizationStatus: 'idle',
  initialImportBackgroundCategorizationTotalCount: 0,
  initialImportBackgroundCategorizationProcessedCount: 0,
  initialImportBackgroundCategorizationAppliedCount: 0,
  initialImportBackgroundCategorizationCurrentMonthAppliedCount: 0,
  initialImportBackgroundCategorizationDiscardedCount: 0,
  initialImportBackgroundCategorizationFailedCount: 0,
  initialImportBackgroundCategorizationRunning: false,
  initialImportBackgroundCategorizationCompletedAt: null,
  initialImportBackgroundCategorizationResolvedCount: 0,
  initialImportBackgroundCategorizationRemainingCount: 0,
  initialImportBackgroundCategorizationHadUpdates: false,
  initialImportBackgroundCategorizationRecentEvents: [],
};

export const normalizeCanonicalBankStatus = (data = {}) => {
  const canonical = data.onboardingImportStatus;
  if (canonical?.snapshotObservedAt) {
    // Compatibility fields are projections of this snapshot, never competing
    // state captured before the consent decision or worker commit.
    const background = canonical.backgroundCategorization;
    data = { ...data, ...canonical, onboardingImportStatus: canonical,
      onboardingImportStartedAt: canonical.startedAt ?? data.onboardingImportStartedAt,
      budgetAutoBuildReady: canonical.canUseBudgetAutobuild,
    };
    if (background) {
      Object.entries(background).forEach(([key, value]) => {
        data[`initialImportBackgroundCategorization${key[0].toUpperCase()}${key.slice(1)}`] = value;
      });
    }
  }
  return ({
  ...INITIAL_CANONICAL_BANK_STATUS,
  ...data,
  connected: Boolean(data.connected),
  lastSyncAt: data.lastSyncAt || null,
  linkedAccountsCount: toNumberOrFallback(
    data.linkedAccountsCount,
    INITIAL_CANONICAL_BANK_STATUS.linkedAccountsCount
  ),
  lastSyncNewCount: data.lastSyncNewCount !== undefined && data.lastSyncNewCount !== null
    ? toNumberOrFallback(data.lastSyncNewCount, null)
    : null,
  lastSyncUncategorizedCount: data.lastSyncUncategorizedCount !== undefined && data.lastSyncUncategorizedCount !== null
    ? toNumberOrFallback(data.lastSyncUncategorizedCount, null)
    : null,
  lastSyncTransactionIds: Array.isArray(data.lastSyncTransactionIds) ? data.lastSyncTransactionIds : null,
  lastSyncStatus: data.lastSyncStatus || null,
  lastSyncSource: data.lastSyncSource || null,
  needsReconnect: Boolean(data.needsReconnect),
  reconnectItemId: data.reconnectItemId || null,
  hasSyncError: Boolean(data.hasSyncError),
  syncErrorItemId: data.syncErrorItemId || null,
  needsAccountReview: Boolean(data.needsAccountReview),
  reviewItemId: data.reviewItemId || null,
  onboardingImportState: data.onboardingImportState || INITIAL_CANONICAL_BANK_STATUS.onboardingImportState,
  onboardingImportStartedAt: data.onboardingImportStartedAt || null,
  currentImportTransactionCount: toNumberOrFallback(data.currentImportTransactionCount, null),
  recentImportReady: Boolean(data.recentImportReady),
  connectionConfirmed: Boolean(data.connectionConfirmed),
  connectedAccountsReady: Boolean(data.connectedAccountsReady),
  importQueued: Boolean(data.importQueued),
  importRunning: Boolean(data.importRunning),
  recentTransactionsImported: Boolean(data.recentTransactionsImported),
  recentHistoryReady: Boolean(data.recentHistoryReady),
  datasetState: data.datasetState || INITIAL_CANONICAL_BANK_STATUS.datasetState,
  datasetSettled: Boolean(data.datasetSettled),
  datasetHasPersistedData: Boolean(data.datasetHasPersistedData),
  reportingDataReady: Boolean(data.reportingDataReady),
  currentMonthKey: data.currentMonthKey || null,
  currentMonthReady: Boolean(data.currentMonthReady),
  currentMonthTransactionCount: toNumberOrFallback(data.currentMonthTransactionCount, 0),
  importedMonthKeys: Array.isArray(data.importedMonthKeys) ? data.importedMonthKeys : [],
  olderMonthsReady: Boolean(data.olderMonthsReady),
  dashboardDataReady: Boolean(data.dashboardDataReady),
  dashboardReady: Boolean(data.dashboardReady),
  reviewQueueReady: Boolean(data.reviewQueueReady),
  canContinueOnboardingFromImportStep: Boolean(data.canContinueOnboardingFromImportStep),
  historicalImportStatus: data.historicalImportStatus || INITIAL_CANONICAL_BANK_STATUS.historicalImportStatus,
  historicalImportInProgress: Boolean(data.historicalImportInProgress),
  importTickerEvents: Array.isArray(data.importTickerEvents) ? data.importTickerEvents : [],
  historicalImportComplete: Boolean(data.historicalImportComplete),
  budgetAutoBuildReady: Boolean(data.budgetAutoBuildReady),
  transactionImportLocked: Boolean(data.transactionImportLocked),
  aiRefinementLocked: Boolean(data.aiRefinementLocked),
  dataPreparationLocked: Boolean(data.dataPreparationLocked),
  reportsReady: Boolean(data.reportsReady),
  hasExplicitTransactionImportLocked: hasExplicitCanonicalField(
    data,
    'transactionImportLocked',
    'hasExplicitTransactionImportLocked'
  ),
  hasExplicitAiRefinementLocked: hasExplicitCanonicalField(
    data,
    'aiRefinementLocked',
    'hasExplicitAiRefinementLocked'
  ),
  hasExplicitDataPreparationLocked: hasExplicitCanonicalField(
    data,
    'dataPreparationLocked',
    'hasExplicitDataPreparationLocked'
  ),
  hasExplicitReportsReady: hasExplicitCanonicalField(
    data,
    'reportsReady',
    'hasExplicitReportsReady'
  ),
  hasExplicitBankImportMutationLocked: hasExplicitCanonicalField(
    data,
    'bankImportMutationLocked',
    'hasExplicitBankImportMutationLocked'
  ),
  bankImportMutationLocked: Boolean(data.bankImportMutationLocked),
  aiCategorizationPromptReady: data.aiCategorizationPromptReady === true,
  onboardingImportStatus: {
    ...INITIAL_CANONICAL_BANK_STATUS.onboardingImportStatus,
    ...(data.onboardingImportStatus || {}),
    datasetState: data.onboardingImportStatus?.datasetState
      || INITIAL_CANONICAL_BANK_STATUS.onboardingImportStatus.datasetState,
    datasetSettled: Boolean(data.onboardingImportStatus?.datasetSettled),
    datasetHasPersistedData: Boolean(data.onboardingImportStatus?.datasetHasPersistedData),
    reportingDataReady: Boolean(data.onboardingImportStatus?.reportingDataReady),
    transactionImportLocked: Boolean(data.onboardingImportStatus?.transactionImportLocked),
    aiRefinementLocked: Boolean(data.onboardingImportStatus?.aiRefinementLocked),
    dataPreparationLocked: Boolean(data.onboardingImportStatus?.dataPreparationLocked),
    reportsReady: Boolean(data.onboardingImportStatus?.reportsReady),
    importedMonthKeys: Array.isArray(data.onboardingImportStatus?.importedMonthKeys)
      ? data.onboardingImportStatus.importedMonthKeys
      : INITIAL_CANONICAL_BANK_STATUS.onboardingImportStatus.importedMonthKeys,
    activeInstitutionName: typeof data.onboardingImportStatus?.activeInstitutionName === 'string'
      ? data.onboardingImportStatus.activeInstitutionName.trim() || null
      : null,
    queuedImportCount: Math.max(0, toNumberOrFallback(
      data.onboardingImportStatus?.queuedImportCount
        ?? data.onboardingImportStatus?.queuedImportsCount
        ?? data.onboardingImportStatus?.queuedInstitutionCount
        ?? data.onboardingImportStatus?.queuedInstitutionsCount,
      INITIAL_CANONICAL_BANK_STATUS.onboardingImportStatus.queuedImportCount
    )),
  },
  initialImportBackgroundCategorizationStatus:
    data.initialImportBackgroundCategorizationStatus || INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationStatus,
  initialImportBackgroundCategorizationTotalCount: toNumberOrFallback(
    data.initialImportBackgroundCategorizationTotalCount,
    INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationTotalCount
  ),
  initialImportBackgroundCategorizationProcessedCount: toNumberOrFallback(
    data.initialImportBackgroundCategorizationProcessedCount,
    INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationProcessedCount
  ),
  initialImportBackgroundCategorizationAppliedCount: toNumberOrFallback(
    data.initialImportBackgroundCategorizationAppliedCount,
    INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationAppliedCount
  ),
  initialImportBackgroundCategorizationCurrentMonthAppliedCount: toNumberOrFallback(
    data.initialImportBackgroundCategorizationCurrentMonthAppliedCount,
    INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationCurrentMonthAppliedCount
  ),
  initialImportBackgroundCategorizationDiscardedCount: toNumberOrFallback(
    data.initialImportBackgroundCategorizationDiscardedCount,
    INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationDiscardedCount
  ),
  initialImportBackgroundCategorizationFailedCount: toNumberOrFallback(
    data.initialImportBackgroundCategorizationFailedCount,
    INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationFailedCount
  ),
  initialImportBackgroundCategorizationRunning: Boolean(data.initialImportBackgroundCategorizationRunning),
  initialImportBackgroundCategorizationCompletedAt: data.initialImportBackgroundCategorizationCompletedAt || null,
  initialImportBackgroundCategorizationResolvedCount: toNumberOrFallback(
    data.initialImportBackgroundCategorizationResolvedCount,
    INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationResolvedCount
  ),
  initialImportBackgroundCategorizationRemainingCount: toNumberOrFallback(
    data.initialImportBackgroundCategorizationRemainingCount,
    INITIAL_CANONICAL_BANK_STATUS.initialImportBackgroundCategorizationRemainingCount
  ),
  initialImportBackgroundCategorizationHadUpdates: Boolean(data.initialImportBackgroundCategorizationHadUpdates),
  initialImportBackgroundCategorizationRecentEvents: Array.isArray(data.initialImportBackgroundCategorizationRecentEvents)
    ? data.initialImportBackgroundCategorizationRecentEvents
    : [],
  });
};
